import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    eventType: 'all',
    status: 'published',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchEvents();
  }, [filters]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await apiService.getEvents(filters);
      setEvents(response.data.events);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch events');
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (eventId, newStatus) => {
    try {
      await apiService.updateEventStatus(eventId, newStatus);
      setSuccess('Event status updated successfully');
      fetchEvents();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to update event status');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleFeatureToggle = async (eventId) => {
    try {
      await apiService.toggleEventFeature(eventId);
      setSuccess('Event featured status updated');
      fetchEvents();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to update featured status');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      await apiService.deleteEvent(eventId);
      setSuccess('Event deleted successfully');
      fetchEvents();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to delete event');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEventTypeIcon = (eventType) => {
    switch (eventType) {
      case 'seminar': return 'fas fa-presentation';
      case 'workshop': return 'fas fa-tools';
      case 'conference': return 'fas fa-users';
      case 'cultural': return 'fas fa-theater-masks';
      case 'sports': return 'fas fa-trophy';
      case 'technical': return 'fas fa-code';
      case 'academic': return 'fas fa-graduation-cap';
      case 'placement': return 'fas fa-briefcase';
      default: return 'fas fa-calendar';
    }
  };

  return (
    <div className="font-sans">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-sans font-bold text-slate mb-2">Events Management</h1>
          <p className="text-gray-600">Create, manage and publish events for the college community.</p>
        </div>
        <Link 
          to="/admin/events/new" 
          className="px-6 py-3 bg-crimson text-white rounded-lg font-medium hover:bg-crimson/90 flex items-center gap-2"
        >
          <i className="fas fa-plus"></i>
          New Event
        </Link>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-check-circle mr-2"></i>
          {success}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate mb-2">Event Type</label>
            <select
              value={filters.eventType}
              onChange={(e) => setFilters(prev => ({ ...prev, eventType: e.target.value, page: 1 }))}
              className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
            >
              <option value="all">All Types</option>
              <option value="seminar">Seminar</option>
              <option value="workshop">Workshop</option>
              <option value="conference">Conference</option>
              <option value="cultural">Cultural</option>
              <option value="sports">Sports</option>
              <option value="technical">Technical</option>
              <option value="academic">Academic</option>
              <option value="placement">Placement</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
              className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
            >
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate mb-2">Items per page</label>
            <select
              value={filters.limit}
              onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
              className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Events List */}
      <div className="bg-white rounded-lg border border-border_gray shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-lg text-slate">
              <i className="fas fa-spinner fa-spin mr-2 text-crimson"></i>
              Loading events...
            </div>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <i className="fas fa-calendar text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No events found</h3>
            <p className="text-gray-500 mb-4">Create your first event to get started.</p>
            <Link 
              to="/admin/events/new" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90"
            >
              <i className="fas fa-plus"></i>
              Create Event
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {events.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-slate truncate">{event.title}</p>
                            {event.featured && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                <i className="fas fa-star mr-1"></i>
                                Featured
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 truncate">{event.venue}</p>
                          <p className="text-xs text-gray-400">Views: {event.views || 0}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center space-x-1">
                        <i className={`${getEventTypeIcon(event.eventType)} text-gray-400`}></i>
                        <span className="text-sm text-gray-900 capitalize">{event.eventType}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(event.startDate)}
                      {event.endDate && event.startDate !== event.endDate && (
                        <span> - {formatDate(event.endDate)}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={event.status}
                        onChange={(e) => handleStatusChange(event.id, e.target.value)}
                        className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusBadgeClass(event.status)}`}
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="completed">Completed</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <Link 
                        to={`/admin/events/edit/${event.id}`} 
                        className="text-crimson hover:text-red-800"
                      >
                        <i className="fas fa-edit"></i>
                      </Link>
                      <button
                        onClick={() => handleFeatureToggle(event.id)}
                        className={`${event.featured ? 'text-yellow-600' : 'text-gray-400'} hover:text-yellow-700`}
                        title={event.featured ? 'Remove from featured' : 'Add to featured'}
                      >
                        <i className="fas fa-star"></i>
                      </button>
                      <button
                        onClick={() => handleDelete(event.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing page {pagination.current} of {pagination.total} ({pagination.totalItems} total events)
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setFilters(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.current === 1}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters(prev => ({ ...prev, page: Math.min(pagination.total, prev.page + 1) }))}
                disabled={pagination.current === pagination.total}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;